// src/pages/AdminVendorRequests.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db, getSecondaryAuth, functions } from "../../firebase";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  setDoc,
  increment,
  Timestamp,
} from "firebase/firestore";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { httpsCallable } from "firebase/functions";

const PAGE_SIZE = 8;


const AFFILIATE_RULES = {
  standard: {
    type: "commission_based",
    label: "Commission sur activité",
    earningAmount: 0,
    percentOnGoShopCommission: 10,
    description:
      "L’affilié gagne 10% des commissions GoShop générées par ce vendeur.",
  },
  standard_plus: {
    type: "fixed_pack_bonus",
    label: "Prime Pack Standard+",
    earningAmount: 1000,
    percentOnGoShopCommission: 0,
    description:
      "L’affilié gagne une prime fixe après paiement du forfait par le vendeur.",
  },
  pro_semestre: {
    type: "fixed_pack_bonus",
    label: "Prime Pack Pro Semestriel",
    earningAmount: 2000,
    percentOnGoShopCommission: 0,
    description:
      "L’affilié gagne une prime fixe après paiement du forfait par le vendeur.",
  },
  pro: {
    type: "fixed_pack_bonus",
    label: "Prime Pack Pro Annuel",
    earningAmount: 3000,
    percentOnGoShopCommission: 0,
    description:
      "L’affilié gagne une prime fixe après paiement du forfait par le vendeur.",
  },
};

function getAffiliateRule(packId) {
  return AFFILIATE_RULES[String(packId || "standard").toLowerCase()] || AFFILIATE_RULES.standard;
}

function getAffiliateOwnerUid(req) {
  return (
    req?.affiliateTracking?.ownerUid ||
    req?.affiliate?.ownerUid ||
    req?.affiliateOwnerUid ||
    null
  );
}

function formatMoney(v) {
  const n = Number(v || 0);
  return n.toLocaleString("fr-FR") + " FCFA";
}

function normalizeText(str = "") {
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function statusLabel(s) {
  if (s === "pending_admin_validation") return "En attente";
  if (s === "approved") return "Validé";
  if (s === "rejected") return "Refusé";
  return s || "—";
}

function isPaidPack(packId) {
  return ["standard_plus", "pro_semestre", "pro"].includes(
    String(packId || "").toLowerCase()
  );
}

function generateTempPassword() {
  const chars =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@$%&*";
  let pwd = "";
  for (let i = 0; i < 12; i++) {
    pwd += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return pwd;
}

function generateOnboardingToken(length = 40) {
  const chars =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  let token = "";
  for (let i = 0; i < length; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

function toDateSafe(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (value?.toDate) return value.toDate();
  return null;
}

function formatDateTime(value) {
  const d = toDateSafe(value);
  if (!d) return "—";
  return d.toLocaleString("fr-FR");
}

function isOnboardingExpired(req) {
  const expiresAt =
    toDateSafe(req?.onboardingTokenExpiresAt) ||
    toDateSafe(req?.tokenExpiresAt) ||
    toDateSafe(req?.onboardingExpiresAt);

  if (!expiresAt) return false;
  return expiresAt.getTime() < Date.now();
}

function getOnboardingExpiryLabel(req) {
  const expiresAt =
    toDateSafe(req?.onboardingTokenExpiresAt) ||
    toDateSafe(req?.tokenExpiresAt) ||
    toDateSafe(req?.onboardingExpiresAt);

  if (!expiresAt) return "Non définie";

  if (expiresAt.getTime() < Date.now()) {
    return `Expiré le ${expiresAt.toLocaleString("fr-FR")}`;
  }

  return `Expire le ${expiresAt.toLocaleString("fr-FR")}`;
}

export default function AdminVendorRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [regenLoadingId, setRegenLoadingId] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [previewReq, setPreviewReq] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const appBaseUrl = window.location.origin || "http://localhost:3000";

  const loadRequests = async () => {
    setLoading(true);
    setError(null);

    try {
      const qRef = query(
        collection(db, "vendorRequests"),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(qRef);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRequests(list);
    } catch (err) {
      console.error(err);
      setError("Impossible de charger les demandes vendeurs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const copyToClipboard = async (text) => {
    try {
      if (!text) return;
      await navigator.clipboard.writeText(text);
      setInfo("Copié ✅");
      setTimeout(() => setInfo(null), 1500);
    } catch {
      setInfo("Impossible de copier.");
      setTimeout(() => setInfo(null), 1500);
    }
  };

  const handleReject = async (req) => {
    if (!window.confirm(`Refuser la demande de "${req.shopName}" ?`)) return;

    setActionLoadingId(req.id);
    setError(null);
    setInfo(null);

    try {
      await updateDoc(doc(db, "vendorRequests", req.id), {
        status: "rejected",
        updatedAt: serverTimestamp(),
      });

      setInfo("Demande refusée.");
      await loadRequests();
    } catch (err) {
      console.error(err);
      setError("Erreur lors du refus de la demande.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleApprove = async (req) => {
    if (!req.email) {
      alert(
        "Cette demande n'a pas d'email. Demande au vendeur d'ajouter un email."
      );
      return;
    }

    if (req.status === "approved" && req.vendorUid) {
      alert("Cette demande est déjà approuvée.");
      return;
    }

    if (
      !window.confirm(
        `Valider "${req.shopName}" et créer un parcours vendeur pour ${req.email} ?`
      )
    ) {
      return;
    }

    setActionLoadingId(req.id);
    setError(null);
    setInfo(null);

    try {
      const secondaryAuth = getSecondaryAuth();
      const tempPassword = generateTempPassword();
      const onboardingToken = generateOnboardingToken();
      const paidPack = isPaidPack(req.packId);

      const expiresAt = Timestamp.fromDate(
        new Date(Date.now() + 1000 * 60 * 60 * 24 * 2)
      ); // 48h

      const cred = await createUserWithEmailAndPassword(
        secondaryAuth,
        req.email,
        tempPassword
      );
      const newUser = cred.user;

      const onboardingLink = `${appBaseUrl}/vendor/onboarding?token=${onboardingToken}`;

      await setDoc(
        doc(db, "users", newUser.uid),
        {
          uid: newUser.uid,
          email: req.email,
          displayName: req.shopName || null,
          role: "vendor",
          providerId: "password",
          phone: req.phone || null,
          city: req.city || null,

          shopName: req.shopName || null,
          categories: req.categories || null,
          logoUrl: req.logoUrl || null,
          bannerUrl: req.bannerUrl || null,

          packId: req.packId || "standard",
          packName: req.packName || null,
          monthlyFee: req.monthlyFee || 0,
          totalFee: req.totalFee || 0,
          commissionRate: paidPack ? 0 : req.commissionRate || 0,

          isVendor: true,
          status: "active",

          accessStatus: paidPack
            ? "awaiting_payment"
            : "awaiting_password_setup",
          passwordSetupStatus: "pending",
          paymentStatus: paidPack ? "pending" : "not_required",
          subscriptionStatus: paidPack ? "pending_payment" : "active",

          referredBy: req.affiliateRef || null,
          affiliateTracking: req.affiliateTracking || null,
          affiliateRef: req.affiliateRef || null,

          onboardingToken,
          onboardingLink,
          onboardingTokenExpiresAt: expiresAt,

          nextBillingDate: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await setDoc(
        doc(db, "vendorOnboarding", req.id),
        {
          requestId: req.id,
          vendorUid: newUser.uid,
          email: req.email,
          shopName: req.shopName || null,
          packId: req.packId || "standard",
          packName: req.packName || null,
          totalFee: req.totalFee || req.monthlyFee || 0,
          commissionRate: paidPack ? 0 : req.commissionRate || 0,

          affiliateRef: req.affiliateRef || null,
          affiliateTracking: req.affiliateTracking || null,

          requiresPayment: paidPack,
          requiresPasswordSetup: true,

          paymentStatus: paidPack ? "pending" : "not_required",
          passwordSetupStatus: "pending",
          onboardingStatus: paidPack
            ? "approved_pending_payment"
            : "approved_pending_password",

          token: onboardingToken,
          onboardingLink,
          tokenUsed: false,
          tokenExpiresAt: expiresAt,

          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await updateDoc(doc(db, "vendorRequests", req.id), {
        status: "approved",
        vendorUid: newUser.uid,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),

        onboardingToken,
        onboardingLink,
        onboardingTokenExpiresAt: expiresAt,
        onboardingStatus: paidPack
          ? "approved_pending_payment"
          : "approved_pending_password",

        affiliateEarningStatus: req.affiliateRef
          ? paidPack
            ? "pending_vendor_payment"
            : "active_commission_tracking"
          : null,
      });

      const affiliateRule = getAffiliateRule(req.packId);
      const affiliateOwnerUid = getAffiliateOwnerUid(req);

      if (req.affiliateRef && affiliateRule) {
        const earningStatus = paidPack
          ? "pending_vendor_payment"
          : "active_commission_tracking";

        await setDoc(
          doc(db, "affiliateEarnings", `${req.affiliateRef}_${req.id}`),
          {
            affiliateCode: req.affiliateRef,
            affiliateOwnerUid,

            vendorRequestId: req.id,
            newVendorUid: newUser.uid,
            newVendorShopName: req.shopName || null,
            newVendorEmail: req.email || null,
            newVendorPhone: req.phone || null,

            packId: req.packId || "standard",
            packName: req.packName || "Pack Standard",
            packAmount: req.totalFee || req.monthlyFee || 0,

            goShopCommissionRate: paidPack ? 0 : req.commissionRate || 0,

            earningType: affiliateRule.type,
            earningLabel: affiliateRule.label,
            earningDescription: affiliateRule.description,
            earningAmount: affiliateRule.earningAmount || 0,
            percentOnGoShopCommission:
              affiliateRule.percentOnGoShopCommission || 0,

            earningStatus,
            paymentStatus: paidPack ? "waiting_vendor_pack_payment" : "not_applicable",
            approvedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        await updateDoc(doc(db, "affiliateLinks", req.affiliateRef), {
          totalValidatedVendors: increment(1),
          updatedAt: serverTimestamp(),
        });

        if (affiliateOwnerUid) {
          await updateDoc(doc(db, "users", affiliateOwnerUid), {
            "affiliateStats.validatedVendors": increment(1),
            updatedAt: serverTimestamp(),
          });
        }
      }

      setInfo(
        paidPack
          ? "Vendeur créé ✅ Lien d’onboarding généré. Le vendeur doit d’abord payer puis définir son mot de passe."
          : "Vendeur créé ✅ Lien d’onboarding généré. Le vendeur peut maintenant définir son mot de passe."
      );

      await loadRequests();
    } catch (err) {
      console.error(err);
      setError("Erreur lors de la validation / création du compte vendeur.");
    } finally {
      setActionLoadingId(null);
    }
  };

  // const handleRegenerateOnboarding = async (req) => {
  //   if (!req.vendorUid && !req.id) {
  //     alert("Impossible de régénérer le lien pour cette demande.");
  //     return;
  //   }

  //   if (
  //     !window.confirm(
  //       `Régénérer un nouveau lien d'onboarding pour "${req.shopName}" ?`
  //     )
  //   ) {
  //     return;
  //   }

  //   setRegenLoadingId(req.id);
  //   setError(null);
  //   setInfo(null);

  //   try {
  //     const fn = httpsCallable(functions, "regenerateVendorOnboardingLink");
  //     const res = await fn({
  //       onboardingId: req.id,
  //       vendorUid: req.vendorUid || "",
  //     });

  //     const newLink = res?.data?.onboardingLink || "";
  //     const expiresAtIso = res?.data?.expiresAt || null;
  //     const expiresAtDate = expiresAtIso ? Timestamp.fromDate(new Date(expiresAtIso)) : null;

  //     await updateDoc(doc(db, "vendorRequests", req.id), {
  //       onboardingLink: newLink || req.onboardingLink || null,
  //       onboardingTokenExpiresAt: expiresAtDate || null,
  //       updatedAt: serverTimestamp(),
  //     });

  //     setInfo("Nouveau lien d’onboarding généré ✅");
  //     await loadRequests();
  //   } catch (err) {
  //     console.error(err);
  //     setError(
  //       err?.message || "Impossible de régénérer le lien d’onboarding."
  //     );
  //   } finally {
  //     setRegenLoadingId(null);
  //   }
  // };
  const handleRegenerateOnboarding = async (req) => {
    if (!req.vendorUid && !req.id) {
      alert("Impossible de régénérer le lien pour cette demande.");
      return;
    }

    if (
      !window.confirm(
        `Régénérer un nouveau lien d'onboarding pour "${req.shopName}" ?`
      )
    ) {
      return;
    }

    setRegenLoadingId(req.id);
    setError(null);
    setInfo(null);

    try {
      const fn = httpsCallable(functions, "regenerateVendorOnboardingLink");

      const res = await fn({
        onboardingId: req.id || "",
        vendorUid: req.vendorUid || "",
      });

      const data = res?.data || {};
      const newLink = data.onboardingLink || "";
      const onboardingId = data.onboardingId || req.id || "";
      const createdFromLegacy = data.createdFromLegacy === true;
      const expiresAtIso = data.expiresAt || null;

      const expiresAtDate = expiresAtIso
        ? Timestamp.fromDate(new Date(expiresAtIso))
        : null;

      await updateDoc(doc(db, "vendorRequests", req.id), {
        onboardingLink: newLink || req.onboardingLink || null,
        onboardingTokenExpiresAt: expiresAtDate || null,
        onboardingStatus: "approved_pending_password",
        updatedAt: serverTimestamp(),
      });

      setInfo(
        createdFromLegacy
          ? `Nouveau lien d’onboarding généré ✅ Un document onboarding a été créé automatiquement pour cet ancien vendeur.`
          : `Nouveau lien d’onboarding généré ✅`
      );

      if (previewReq?.id === req.id) {
        setPreviewReq((prev) =>
          prev
            ? {
              ...prev,
              onboardingLink: newLink || prev.onboardingLink || null,
              onboardingTokenExpiresAt: expiresAtDate || prev.onboardingTokenExpiresAt || null,
            }
            : prev
        );
      }

      console.log("onboardingId:", onboardingId);
      await loadRequests();
    } catch (err) {
      console.error(err);

      const message =
        err?.message ||
        err?.details ||
        err?.customData?.message ||
        "Impossible de régénérer le lien d’onboarding.";

      setError(message);
    } finally {
      setRegenLoadingId(null);
    }
  };

  const filteredRequests = useMemo(() => {
    const needle = normalizeText(search);

    return requests.filter((req) => {
      const statusOk =
        statusFilter === "all" ? true : (req.status || "") === statusFilter;

      if (!statusOk) return false;
      if (!needle) return true;

      const haystack = normalizeText(
        [
          req.shopName,
          req.fullName,
          req.phone,
          req.email,
          req.city,
          req.categories,
          req.packName,
          req.packId,
          req.status,
        ]
          .filter(Boolean)
          .join(" ")
      );

      return haystack.includes(needle);
    });
  }, [requests, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const visibleRequests = filteredRequests.slice(start, end);

  const stats = useMemo(() => {
    return {
      total: requests.length,
      pending: requests.filter((r) => r.status === "pending_admin_validation")
        .length,
      approved: requests.filter((r) => r.status === "approved").length,
      rejected: requests.filter((r) => r.status === "rejected").length,
    };
  }, [requests]);

  const getFee = (req) => {
    return req.totalFee || req.monthlyFee || 0;
  };

  return (
    <div className="avr-page">
      <main className="avr-main">
        <header className="avr-header">
          <div>
            <h1 className="avr-title">Demandes vendeurs GoShop</h1>
            <p className="avr-subtitle">
              Valide, refuse et pilote le parcours d’activation des vendeurs avec
              paiement et création du mot de passe via GoShop.
            </p>
          </div>

          <button
            type="button"
            className="avr-refreshBtn"
            onClick={loadRequests}
            disabled={loading}
          >
            {loading ? "Chargement..." : "Actualiser"}
          </button>
        </header>

        {error && <div className="avr-alert avr-alert-error">{error}</div>}
        {info && <div className="avr-alert avr-alert-success">{info}</div>}

        <section className="avr-stats">
          <div className="avr-statCard">
            <span>Total</span>
            <strong>{stats.total}</strong>
          </div>
          <div className="avr-statCard avr-statCard-pending">
            <span>En attente</span>
            <strong>{stats.pending}</strong>
          </div>
          <div className="avr-statCard avr-statCard-approved">
            <span>Validés</span>
            <strong>{stats.approved}</strong>
          </div>
          <div className="avr-statCard avr-statCard-rejected">
            <span>Refusés</span>
            <strong>{stats.rejected}</strong>
          </div>
        </section>

        <section className="avr-toolbarCard">
          <div className="avr-toolbar">
            <input
              type="search"
              className="avr-search"
              placeholder="Rechercher boutique, email, ville, pack..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <select
              className="avr-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tous les statuts</option>
              <option value="pending_admin_validation">En attente</option>
              <option value="approved">Validés</option>
              <option value="rejected">Refusés</option>
            </select>
          </div>

          <div className="avr-toolbarMeta">
            {filteredRequests.length} demande(s) trouvée(s)
          </div>
        </section>

        <section className="avr-tableCard">
          {loading ? (
            <p className="avr-empty">Chargement des demandes…</p>
          ) : filteredRequests.length === 0 ? (
            <p className="avr-empty">Aucune demande trouvée.</p>
          ) : (
            <>
              <div className="avr-tableWrap">
                <table className="avr-table">
                  <thead>
                    <tr>
                      <th className="avr-th">Boutique</th>
                      <th className="avr-th">Identité visuelle</th>
                      <th className="avr-th">Contact</th>
                      <th className="avr-th">Pack</th>
                      <th className="avr-th">Ville</th>
                      <th className="avr-th">Statut</th>
                      <th className="avr-th">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRequests.map((req) => (
                      <tr key={req.id} className="avr-row">
                        <td className="avr-td">
                          <div className="avr-shopName">
                            {req.shopName || "—"}
                          </div>
                          <div className="avr-shopMeta">
                            Catégories : {req.categories || "—"}
                          </div>

                          {req.approvedAt && (
                            <div className="avr-inlineMeta">
                              Validé le : {formatDateTime(req.approvedAt)}
                            </div>
                          )}

                          <div className="avr-shopActions">
                            <button
                              type="button"
                              className="avr-darkBtn"
                              onClick={() => setPreviewReq(req)}
                            >
                              Aperçu boutique
                            </button>

                            <span className="avr-idPill">
                              ID: {req.id.slice(0, 6)}…
                            </span>

                            {req.affiliateRef ? (
                              <span className="avr-affiliatePill">
                                Affiliation: {req.affiliateRef}
                              </span>
                            ) : null}
                          </div>
                        </td>

                        <td className="avr-td">
                          <div className="avr-visualCell">
                            <div className="avr-logoBox">
                              {req.logoUrl ? (
                                <img
                                  src={req.logoUrl}
                                  alt="logo"
                                  className="avr-logoImg"
                                />
                              ) : (
                                <div className="avr-logoFallback">Logo</div>
                              )}
                            </div>

                            <div className="avr-visualMeta">
                              <span>
                                {req.bannerUrl
                                  ? "Bannière OK"
                                  : "Pas de bannière"}
                              </span>

                              {req.bannerUrl ? (
                                <button
                                  type="button"
                                  className="avr-miniSoftBtn"
                                  onClick={() => setPreviewReq(req)}
                                >
                                  Voir bannière
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </td>

                        <td className="avr-td">
                          <div className="avr-contactName">
                            {req.fullName || "—"}
                          </div>

                          <div className="avr-contactLine">
                            <span>{req.phone || "—"}</span>
                            {req.phone ? (
                              <button
                                type="button"
                                className="avr-miniLink"
                                onClick={() => copyToClipboard(req.phone)}
                              >
                                Copier
                              </button>
                            ) : null}
                          </div>

                          <div className="avr-contactLine">
                            <span>{req.email || "—"}</span>
                            {req.email ? (
                              <button
                                type="button"
                                className="avr-miniLink"
                                onClick={() => copyToClipboard(req.email)}
                              >
                                Copier
                              </button>
                            ) : null}
                          </div>

                          {req.onboardingLink ? (
                            <>
                              <div className="avr-contactLine">
                                <button
                                  type="button"
                                  className="avr-miniLink"
                                  onClick={() =>
                                    copyToClipboard(req.onboardingLink)
                                  }
                                >
                                  Copier lien onboarding
                                </button>
                              </div>

                              <div className="avr-inlineMeta">
                                {getOnboardingExpiryLabel(req)}
                              </div>

                              <div className="avr-contactLine">
                                <span
                                  className={
                                    isOnboardingExpired(req)
                                      ? "avr-badge avr-badge-danger"
                                      : "avr-badge avr-badge-ok"
                                  }
                                >
                                  {isOnboardingExpired(req)
                                    ? "Lien expiré"
                                    : "Lien actif"}
                                </span>
                              </div>
                            </>
                          ) : (
                            <div className="avr-inlineMeta">
                              Lien onboarding non généré
                            </div>
                          )}
                        </td>

                        <td className="avr-td">
                          <div className="avr-packName">
                            {req.packName || req.packId || "—"}
                          </div>
                          <div className="avr-packMeta">
                            {formatMoney(getFee(req))}
                          </div>
                          <div className="avr-packMeta">
                            Commission :{" "}
                            {isPaidPack(req.packId)
                              ? 0
                              : req.commissionRate || 0}
                            %
                          </div>

                          {req.affiliateRef ? (
                            <div className="avr-affiliateMeta">
                              {isPaidPack(req.packId)
                                ? `Gain affilié prévu : ${formatMoney(
                                    getAffiliateRule(req.packId).earningAmount
                                  )}`
                                : `Gain affilié : ${
                                    getAffiliateRule(req.packId)
                                      .percentOnGoShopCommission
                                  }% des commissions GoShop`}
                            </div>
                          ) : null}
                        </td>

                        <td className="avr-td">{req.city || "—"}</td>

                        <td className="avr-td">
                          <span
                            className={`avr-status avr-status-${req.status}`}
                          >
                            {statusLabel(req.status)}
                          </span>
                        </td>

                        <td className="avr-td">
                          <div className="avr-actionStack">
                            <button
                              type="button"
                              className="avr-primaryBtn"
                              disabled={
                                actionLoadingId === req.id ||
                                req.status === "approved"
                              }
                              onClick={() => handleApprove(req)}
                            >
                              {actionLoadingId === req.id
                                ? "Traitement..."
                                : "Valider & créer"}
                            </button>

                            <button
                              type="button"
                              className="avr-dangerBtn"
                              disabled={
                                actionLoadingId === req.id ||
                                req.status === "rejected"
                              }
                              onClick={() => handleReject(req)}
                            >
                              Refuser
                            </button>

                            {req.status === "approved" && (
                              <button
                                type="button"
                                className="avr-outlineBtn"
                                disabled={regenLoadingId === req.id}
                                onClick={() =>
                                  handleRegenerateOnboarding(req)
                                }
                              >
                                {regenLoadingId === req.id
                                  ? "Régénération..."
                                  : "Régénérer le lien"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="avr-pagination">
                <div className="avr-paginationInfo">
                  Affichage de <strong>{start + 1}</strong> à{" "}
                  <strong>{Math.min(end, filteredRequests.length)}</strong> sur{" "}
                  <strong>{filteredRequests.length}</strong>
                </div>

                <div className="avr-paginationControls">
                  <button
                    type="button"
                    className="avr-pageBtn"
                    disabled={safePage <= 1}
                    onClick={() => {
                      setCurrentPage((p) => Math.max(1, p - 1));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    ← Précédent
                  </button>

                  <div className="avr-pageCurrent">
                    Page {safePage} / {totalPages}
                  </div>

                  <button
                    type="button"
                    className="avr-pageBtn"
                    disabled={safePage >= totalPages}
                    onClick={() => {
                      setCurrentPage((p) => Math.min(totalPages, p + 1));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    Suivant →
                  </button>
                </div>
              </div>
            </>
          )}
        </section>

        {previewReq && (
          <div
            className="avr-modalBackdrop"
            onMouseDown={() => setPreviewReq(null)}
          >
            <div
              className="avr-modal"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="avr-modalHeader">
                <div>
                  <div className="avr-modalTitle">{previewReq.shopName}</div>
                  <div className="avr-modalSub">
                    {previewReq.city || "—"} • {previewReq.phone || "—"}
                  </div>
                </div>

                <button
                  type="button"
                  className="avr-closeBtn"
                  onClick={() => setPreviewReq(null)}
                >
                  ✕
                </button>
              </div>

              <div className="avr-bannerBox">
                {previewReq.bannerUrl ? (
                  <img
                    src={previewReq.bannerUrl}
                    alt="banner"
                    className="avr-bannerImg"
                  />
                ) : (
                  <div className="avr-bannerFallback">Aucune bannière</div>
                )}

                <div className="avr-modalLogo">
                  {previewReq.logoUrl ? (
                    <img
                      src={previewReq.logoUrl}
                      alt="logo"
                      className="avr-modalLogoImg"
                    />
                  ) : (
                    <div className="avr-modalLogoFallback">Logo</div>
                  )}
                </div>
              </div>

              <div className="avr-modalBody">
                <div className="avr-chipRow">
                  <span className="avr-chip">
                    Pack: {previewReq.packName || previewReq.packId}
                  </span>
                  <span className="avr-chip">
                    Fee: {formatMoney(getFee(previewReq))}
                  </span>
                  <span className="avr-chip">
                    Commission:{" "}
                    {isPaidPack(previewReq.packId)
                      ? 0
                      : previewReq.commissionRate || 0}
                    %
                  </span>
                </div>

                <div className="avr-sectionBlock">
                  <div className="avr-sectionTitle">Catégories</div>
                  <div className="avr-sectionText">
                    {previewReq.categories || "—"}
                  </div>
                </div>

                {previewReq.onboardingLink ? (
                  <div className="avr-sectionBlock">
                    <div className="avr-sectionTitle">Lien onboarding</div>
                    <div className="avr-sectionText avr-linkBox">
                      {previewReq.onboardingLink}
                    </div>

                    <div className="avr-inlineMeta" style={{ marginTop: 8 }}>
                      {getOnboardingExpiryLabel(previewReq)}
                    </div>

                    <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="avr-copyLinkBtn"
                        onClick={() =>
                          copyToClipboard(previewReq.onboardingLink)
                        }
                      >
                        Copier le lien
                      </button>

                      {previewReq.status === "approved" && (
                        <button
                          type="button"
                          className="avr-outlineBtn"
                          onClick={() => handleRegenerateOnboarding(previewReq)}
                          disabled={regenLoadingId === previewReq.id}
                        >
                          {regenLoadingId === previewReq.id
                            ? "Régénération..."
                            : "Régénérer le lien"}
                        </button>
                      )}
                    </div>
                  </div>
                ) : null}

                <div className="avr-modalActions">
                  <button
                    type="button"
                    className="avr-lightBtn"
                    onClick={() => setPreviewReq(null)}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .avr-page{
            min-height:100vh;
            background:linear-gradient(180deg,#f8fafc 0%, #f3f4f6 100%);
            padding:24px 16px 40px;
            display:flex;
            justify-content:center;
          }

          .avr-main{
            width:100%;
            max-width:1160px;
            font-size:14px;
            line-height:1.5;
          }

          .avr-header{
            display:flex;
            justify-content:space-between;
            align-items:flex-start;
            gap:16px;
            flex-wrap:wrap;
            margin-bottom:16px;
          }

          .avr-title{
            font-size:26px;
            font-weight:900;
            color:#0f172a;
            margin:0 0 6px;
          }

          .avr-subtitle{
            font-size:13px;
            color:#475569;
            margin:0;
            max-width:760px;
          }

          .avr-refreshBtn{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            padding:10px 16px;
            border-radius:999px;
            border:1px solid #dbeafe;
            background:#eff6ff;
            color:#1d4ed8;
            font-size:13px;
            font-weight:800;
            cursor:pointer;
          }

          .avr-refreshBtn:hover:not(:disabled){
            background:#dbeafe;
          }

          .avr-alert{
            margin-bottom:12px;
            padding:10px 12px;
            border-radius:12px;
            font-size:13px;
            font-weight:600;
          }

          .avr-alert-error{
            background:#fef2f2;
            color:#b91c1c;
            border:1px solid #fecaca;
          }

          .avr-alert-success{
            background:#ecfdf3;
            color:#166534;
            border:1px solid #bbf7d0;
          }

          .avr-stats{
            display:grid;
            grid-template-columns:repeat(4, minmax(0,1fr));
            gap:12px;
            margin-bottom:16px;
          }

          .avr-statCard{
            background:#fff;
            border:1px solid #e5e7eb;
            border-radius:18px;
            padding:14px 16px;
            box-shadow:0 12px 28px rgba(15,23,42,.05);
          }

          .avr-statCard span{
            display:block;
            font-size:12px;
            color:#64748b;
            margin-bottom:6px;
          }

          .avr-statCard strong{
            font-size:24px;
            color:#0f172a;
            font-weight:900;
          }

          .avr-statCard-pending{
            background:linear-gradient(180deg,#fffef2 0%, #ffffff 100%);
          }

          .avr-statCard-approved{
            background:linear-gradient(180deg,#f0fdf4 0%, #ffffff 100%);
          }

          .avr-statCard-rejected{
            background:linear-gradient(180deg,#fff1f2 0%, #ffffff 100%);
          }

          .avr-toolbarCard,
          .avr-tableCard{
            background:#ffffff;
            border-radius:22px;
            border:1px solid #e5e7eb;
            padding:14px;
            box-shadow:0 14px 34px rgba(15,23,42,.07);
          }

          .avr-toolbarCard{
            margin-bottom:14px;
          }

          .avr-toolbar{
            display:flex;
            justify-content:space-between;
            align-items:center;
            gap:10px;
            flex-wrap:wrap;
          }

          .avr-search,
          .avr-filter{
            border:1px solid #d1d5db;
            border-radius:14px;
            background:#fff;
            padding:10px 12px;
            font-size:13px;
            outline:none;
          }

          .avr-search{
            min-width:320px;
            flex:1;
          }

          .avr-search:focus,
          .avr-filter:focus{
            border-color:#2563eb;
            box-shadow:0 0 0 3px rgba(37,99,235,.08);
          }

          .avr-toolbarMeta{
            margin-top:10px;
            font-size:12px;
            color:#64748b;
            font-weight:700;
          }

          .avr-tableWrap{
            overflow-x:auto;
          }

          .avr-table{
            width:100%;
            min-width:1080px;
            border-collapse:collapse;
            font-size:13px;
          }

          .avr-th{
            text-align:left;
            padding:10px 10px;
            border-bottom:1px solid #e5e7eb;
            font-size:12px;
            color:#64748b;
            font-weight:800;
            background:#f8fafc;
          }

          .avr-td{
            padding:12px 10px;
            border-bottom:1px solid #f1f5f9;
            vertical-align:top;
          }

          .avr-row:hover{
            background:#fafafa;
          }

          .avr-shopName{
            font-weight:900;
            color:#0f172a;
            font-size:14px;
          }

          .avr-shopMeta{
            font-size:12px;
            color:#64748b;
            margin-top:4px;
          }

          .avr-inlineMeta{
            margin-top:6px;
            font-size:11px;
            color:#64748b;
          }

          .avr-shopActions{
            margin-top:10px;
            display:flex;
            gap:8px;
            flex-wrap:wrap;
            align-items:center;
          }

          .avr-darkBtn{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            padding:6px 12px;
            border-radius:999px;
            border:none;
            background:#0f172a;
            color:#fff;
            font-size:12px;
            font-weight:800;
            cursor:pointer;
          }

          .avr-darkBtn:hover{
            background:#111827;
          }

          .avr-idPill{
            display:inline-flex;
            align-items:center;
            padding:4px 10px;
            border-radius:999px;
            font-size:11px;
            font-weight:900;
            background:#fff7ed;
            color:#c2410c;
            border:1px solid #fed7aa;
          }

          .avr-affiliatePill{
            display:inline-flex;
            align-items:center;
            padding:4px 10px;
            border-radius:999px;
            font-size:11px;
            font-weight:900;
            background:#ecfdf5;
            color:#166534;
            border:1px solid #bbf7d0;
          }

          .avr-affiliateMeta{
            margin-top:6px;
            padding:6px 8px;
            border-radius:10px;
            background:#f0fdf4;
            color:#166534;
            border:1px solid #bbf7d0;
            font-size:11px;
            font-weight:800;
          }

          .avr-visualCell{
            display:flex;
            align-items:center;
            gap:10px;
          }

          .avr-logoBox{
            width:48px;
            height:48px;
            border-radius:16px;
            border:1px solid #e5e7eb;
            background:#fff;
            display:flex;
            align-items:center;
            justify-content:center;
            overflow:hidden;
            box-shadow:0 8px 18px rgba(15,23,42,0.08);
            flex-shrink:0;
          }

          .avr-logoImg{
            width:100%;
            height:100%;
            object-fit:cover;
            display:block;
          }

          .avr-logoFallback{
            font-size:11px;
            font-weight:900;
            color:#64748b;
          }

          .avr-visualMeta{
            display:flex;
            flex-direction:column;
            gap:5px;
            font-size:12px;
            color:#64748b;
          }

          .avr-miniSoftBtn{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            padding:6px 10px;
            border-radius:999px;
            border:1px solid #e5e7eb;
            background:#f8fafc;
            color:#111827;
            font-size:12px;
            font-weight:700;
            cursor:pointer;
          }

          .avr-contactName,
          .avr-packName{
            font-weight:800;
            color:#0f172a;
          }

          .avr-contactLine{
            display:flex;
            gap:8px;
            align-items:center;
            margin-top:5px;
            font-size:12px;
            color:#64748b;
            flex-wrap:wrap;
          }

          .avr-packMeta{
            font-size:12px;
            color:#64748b;
            margin-top:4px;
          }

          .avr-miniLink{
            border:none;
            background:transparent;
            color:#2563eb;
            font-weight:800;
            font-size:12px;
            cursor:pointer;
            padding:0;
          }

          .avr-miniLink:hover{
            text-decoration:underline;
          }

          .avr-badge{
            display:inline-flex;
            align-items:center;
            padding:4px 9px;
            border-radius:999px;
            font-size:11px;
            font-weight:800;
          }

          .avr-badge-ok{
            background:#ecfdf5;
            color:#166534;
            border:1px solid #bbf7d0;
          }

          .avr-badge-danger{
            background:#fef2f2;
            color:#b91c1c;
            border:1px solid #fecaca;
          }

          .avr-status{
            display:inline-flex;
            align-items:center;
            padding:4px 10px;
            border-radius:999px;
            font-size:11px;
            font-weight:900;
          }

          .avr-status-pending_admin_validation{
            background:#fef9c3;
            color:#92400e;
          }

          .avr-status-approved{
            background:#dcfce7;
            color:#166534;
          }

          .avr-status-rejected{
            background:#fee2e2;
            color:#b91c1c;
          }

          .avr-actionStack{
            display:flex;
            flex-direction:column;
            gap:8px;
          }

          .avr-primaryBtn,
          .avr-dangerBtn,
          .avr-outlineBtn{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            padding:8px 14px;
            border-radius:999px;
            font-size:13px;
            font-weight:700;
            cursor:pointer;
          }

          .avr-primaryBtn{
            border:none;
            background:#16a34a;
            color:#fff;
          }

          .avr-primaryBtn:disabled{
            opacity:.65;
            cursor:not-allowed;
          }

          .avr-dangerBtn{
            background:#fff1f2;
            color:#b91c1c;
            border:1px solid #fecdd3;
          }

          .avr-dangerBtn:disabled{
            opacity:.65;
            cursor:not-allowed;
          }

          .avr-outlineBtn{
            background:#eff6ff;
            color:#1d4ed8;
            border:1px solid #bfdbfe;
          }

          .avr-outlineBtn:disabled{
            opacity:.65;
            cursor:not-allowed;
          }

          .avr-empty{
            font-size:13px;
            color:#6b7280;
            margin:0;
            padding:8px 0;
          }

          .avr-pagination{
            margin-top:16px;
            display:flex;
            justify-content:space-between;
            align-items:center;
            gap:12px;
            flex-wrap:wrap;
          }

          .avr-paginationInfo{
            font-size:12px;
            color:#64748b;
          }

          .avr-paginationControls{
            display:flex;
            align-items:center;
            gap:10px;
            flex-wrap:wrap;
          }

          .avr-pageBtn{
            border:1px solid #d1d5db;
            background:#fff;
            color:#111827;
            border-radius:999px;
            padding:8px 14px;
            font-size:12px;
            font-weight:800;
            cursor:pointer;
          }

          .avr-pageBtn:hover:not(:disabled){
            background:#f8fafc;
          }

          .avr-pageBtn:disabled{
            opacity:.5;
            cursor:not-allowed;
          }

          .avr-pageCurrent{
            font-size:12px;
            color:#374151;
            font-weight:800;
          }

          .avr-modalBackdrop{
            position:fixed;
            inset:0;
            background:rgba(2,6,23,.6);
            display:flex;
            align-items:center;
            justify-content:center;
            padding:16px;
            z-index:9999;
          }

          .avr-modal{
            width:min(900px, 100%);
            background:#fff;
            border-radius:22px;
            overflow:hidden;
            border:1px solid rgba(255,255,255,.15);
            box-shadow:0 30px 80px rgba(0,0,0,.35);
          }

          .avr-modalHeader{
            display:flex;
            justify-content:space-between;
            align-items:center;
            gap:12px;
            padding:14px 16px;
            border-bottom:1px solid #e5e7eb;
          }

          .avr-modalTitle{
            font-size:18px;
            font-weight:900;
            color:#0f172a;
          }

          .avr-modalSub{
            font-size:12px;
            color:#64748b;
            margin-top:3px;
          }

          .avr-closeBtn{
            width:38px;
            height:38px;
            border-radius:12px;
            border:1px solid #e5e7eb;
            background:#f8fafc;
            cursor:pointer;
            font-weight:900;
          }

          .avr-bannerBox{
            position:relative;
            background:#0f172a;
            aspect-ratio:16/5;
            min-height:180px;
          }

          .avr-bannerImg{
            width:100%;
            height:100%;
            object-fit:cover;
            display:block;
          }

          .avr-bannerFallback{
            width:100%;
            height:100%;
            display:flex;
            align-items:center;
            justify-content:center;
            color:rgba(255,255,255,.85);
            font-weight:900;
            background:linear-gradient(135deg,#111827,#0b1226);
          }

          .avr-modalLogo{
            position:absolute;
            left:16px;
            bottom:14px;
            width:84px;
            height:84px;
            border-radius:22px;
            background:#fff;
            border:3px solid rgba(255,255,255,.95);
            overflow:hidden;
            display:flex;
            align-items:center;
            justify-content:center;
            box-shadow:0 16px 30px rgba(0,0,0,.25);
          }

          .avr-modalLogoImg{
            width:100%;
            height:100%;
            object-fit:cover;
          }

          .avr-modalLogoFallback{
            font-size:12px;
            font-weight:900;
            color:#111827;
          }

          .avr-modalBody{
            padding:16px;
          }

          .avr-chipRow{
            display:flex;
            gap:8px;
            flex-wrap:wrap;
            margin-bottom:14px;
          }

          .avr-chip{
            display:inline-flex;
            align-items:center;
            padding:5px 10px;
            border-radius:999px;
            background:#f1f5f9;
            color:#0f172a;
            border:1px solid #e2e8f0;
            font-size:12px;
            font-weight:900;
          }

          .avr-sectionBlock{
            margin-top:12px;
          }

          .avr-sectionTitle{
            font-size:13px;
            font-weight:900;
            color:#0f172a;
            margin-bottom:4px;
          }

          .avr-sectionText{
            font-size:13px;
            color:#475569;
          }

          .avr-linkBox{
            background:#f8fafc;
            border:1px solid #e5e7eb;
            border-radius:12px;
            padding:10px 12px;
            word-break:break-all;
          }

          .avr-copyLinkBtn{
            margin-top:8px;
            display:inline-flex;
            align-items:center;
            justify-content:center;
            padding:8px 12px;
            border-radius:999px;
            border:1px solid #dbeafe;
            background:#eff6ff;
            color:#1d4ed8;
            font-size:12px;
            font-weight:800;
            cursor:pointer;
          }

          .avr-modalActions{
            margin-top:18px;
            display:flex;
            justify-content:flex-end;
          }

          .avr-lightBtn{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            padding:9px 14px;
            border-radius:999px;
            border:1px solid #e5e7eb;
            background:#f8fafc;
            color:#111827;
            font-size:13px;
            font-weight:800;
            cursor:pointer;
          }

          .avr-lightBtn:hover{
            background:#f1f5f9;
          }

          @media (max-width: 980px){
            .avr-stats{
              grid-template-columns:repeat(2, minmax(0,1fr));
            }
          }

          @media (max-width: 720px){
            .avr-stats{
              grid-template-columns:minmax(0,1fr);
            }

            .avr-search{
              min-width:100%;
            }

            .avr-toolbar{
              flex-direction:column;
              align-items:stretch;
            }

            .avr-pagination{
              flex-direction:column;
              align-items:flex-start;
            }
          }
        `}</style>
      </main>
    </div>
  );
}